import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Loader2, Building2, CheckCircle, XCircle, Rocket } from 'lucide-react'
import { ConorbitLoader } from '@/components/ui/ConorbitLoader'
import { useToast } from '@/hooks/use-toast'
import { CreateOrgWizard } from './CreateOrgWizard'
import { CreateUserSheet } from './CreateUserSheet'

const LEAD_COLORS: Record<string, string> = {
  new:       'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  qualified: 'bg-green-100 text-green-700',
  closed:    'bg-muted text-muted-foreground',
}

const REQUEST_COLORS: Record<string, string> = {
  pending:     'bg-[#EBF4FF] text-[#1E4E8C]',
  provisioned: 'bg-[#E1F5EE] text-[#0F6E56]',
  rejected:    'bg-[#FEE8E8] text-[#A32D2D]',
}

export function LeadsView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [updatingLead, setUpdatingLead] = useState<string | null>(null)
  const [selected, setSelected] = useState<any | null>(null)
  const [reviewNotes, setReviewNotes] = useState('')
  const [provisionOpen, setProvisionOpen] = useState(false)
  const [provisionRequest, setProvisionRequest] = useState<any | null>(null)
  const [addCcoOrgId, setAddCcoOrgId] = useState('')
  const [addCcoOrgName, setAddCcoOrgName] = useState('')
  const [addCcoOpen, setAddCcoOpen] = useState(false)

  const { data: leads, isLoading: leadsLoading } = useQuery({
    queryKey: ['enterprise-leads'],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('enterprise_leads')
        .select('*')
        .order('created_at', { ascending: false })
      return (data ?? []) as any[]
    },
  })

  const { data: requests, isLoading: requestsLoading } = useQuery({
    queryKey: ['institution-requests'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('institution_requests' as any)
        .select('*')
        .order('created_at', { ascending: false }) as any)
      return (data ?? []) as any[]
    },
  })

  const updateLeadStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      setUpdatingLead(id)
      const { error } = await (supabase as any).from('enterprise_leads').update({ status }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['enterprise-leads'] }),
    onError: (err: any) => toast({ title: 'Update failed', description: err.message, variant: 'destructive' }),
    onSettled: () => setUpdatingLead(null),
  })

  const approveRequest = useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await (supabase
        .from('institution_requests' as any)
        .update({
          status: approve ? 'provisioned' : 'rejected',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id) as any)
      if (error) throw error
    },
    onSuccess: (_, { approve }) => {
      toast({ title: approve ? 'Request approved.' : 'Request rejected.' })
      qc.invalidateQueries({ queryKey: ['institution-requests'] })
      setSelected(null)
      setReviewNotes('')
    },
    onError: (err: any) => toast({ title: 'Action failed', description: err.message, variant: 'destructive' }),
  })

  const pendingCount = (requests || []).filter((r: any) => r.status === 'pending').length

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-[17px] font-semibold text-[#0D2240] dark:text-white">Leads & Requests</h2>
        <p className="text-justify text-[12px] text-muted-foreground mt-0.5">
          Enterprise demo requests and institution sign-up requests.
        </p>
      </div>

      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests" className="relative">
            Institution Requests
            {pendingCount > 0 && (
              <span className="ml-2 bg-red-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5">
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="enterprise">Enterprise Leads</TabsTrigger>
        </TabsList>

        {/* ── INSTITUTION REQUESTS ── */}
        <TabsContent value="requests" className="mt-4">
          {requestsLoading ? (
            <ConorbitLoader variant="inline" />
          ) : (!requests || requests.length === 0) ? (
            <div className="text-center py-16 text-[13px] text-muted-foreground border rounded-xl">
              <Building2 size={28} className="mx-auto mb-2 opacity-30" />
              No institution requests yet. They appear when someone submits the sign-up form on the landing page.
            </div>
          ) : (
            <div className="border rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Organisation</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type / Country</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(requests || []).map((r: any) => (
                    <TableRow key={r.id} className="cursor-pointer hover:bg-muted/30" onClick={() => { setSelected(r); setReviewNotes(r.review_notes || '') }}>
                      <TableCell>
                        <div className="text-[13px] font-medium">{r.org_name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-[12px]">{r.full_name}</div>
                        <div className="text-[11px] text-muted-foreground">{r.email}</div>
                        {r.phone && <div className="text-[11px] text-muted-foreground">{r.phone}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="text-[12px]">{r.org_type ? r.org_type.replace('_', ' ') : ''}</div>
                        {r.country && <div className="text-[11px] text-muted-foreground">{r.country}</div>}
                      </TableCell>
                      <TableCell className="text-[11.5px] text-muted-foreground whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <span className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full ${REQUEST_COLORS[r.status] ?? 'bg-muted'}`}>
                          {r.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {r.status === 'pending' && (
                          <span className="text-[11px] text-[#1E4E8C] font-medium">Review →</span>
                        )}
                        {r.status === 'provisioned' && !r.provisioned_org_id && (
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              setProvisionRequest(r)
                              setProvisionOpen(true)
                            }}
                            className="flex items-center gap-1 text-[11px] text-white bg-[#0D2240] px-2 py-1 rounded font-medium hover:bg-[#0a1a30]"
                          >
                            <Rocket size={11} /> Provision
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* ── ENTERPRISE LEADS ── */}
        <TabsContent value="enterprise" className="mt-4">
          {leadsLoading ? (
            <ConorbitLoader variant="inline" />
          ) : (!leads || leads.length === 0) ? (
            <div className="text-center py-16 text-[13px] text-muted-foreground border rounded-xl">
              No enterprise leads yet. They appear when companies submit the demo request form on /enterprise.
            </div>
          ) : (
            <div className="border rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Team size</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(leads || []).map((lead: any) => (
                    <TableRow key={lead.id}>
                      <TableCell className="text-[13px] font-medium">{lead.company_name}</TableCell>
                      <TableCell>
                        <div className="text-[12px]">{lead.email}</div>
                        {lead.phone && <div className="text-[11px] text-muted-foreground">{lead.phone}</div>}
                      </TableCell>
                      <TableCell className="text-[12px]">{lead.team_size ?? ''}</TableCell>
                      <TableCell className="max-w-[220px]">
                        <p className="text-justify text-[11.5px] text-muted-foreground line-clamp-2">{lead.message ?? ''}</p>
                      </TableCell>
                      <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {updatingLead === lead.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Select value={lead.status} onValueChange={val => updateLeadStatus.mutate({ id: lead.id, status: val })}>
                            <SelectTrigger className="w-28 h-7 text-xs">
                              <SelectValue>
                                <Badge className={`text-xs ${LEAD_COLORS[lead.status] ?? ''}`}>{lead.status}</Badge>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="qualified">Qualified</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Request review sheet ── */}
      <Sheet open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full max-w-md overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-[15px]">{selected?.org_name}</SheetTitle>
          </SheetHeader>

          <div className="space-y-4">
            <div className="bg-muted/30 rounded-xl p-4 space-y-2 text-[12.5px]">
              <Row label="Full Name" value={selected?.full_name} />
              <Row label="Email" value={selected?.email} />
              {selected?.phone && <Row label="Phone" value={selected.phone} />}
              <Row label="Org Type" value={selected?.org_type?.replace('_', ' ') ?? ''} />
              <Row label="Country" value={selected?.country ?? ''} />
              <Row label="Requested" value={selected?.created_at ? new Date(selected.created_at).toLocaleString() : ''} />
              <Row label="Status" value={selected?.status ?? ''} />
            </div>

            {selected?.status === 'pending' && (
              <>
                <div>
                  <label className="text-[12px] font-medium text-muted-foreground block mb-1">Review Notes (optional)</label>
                  <textarea
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    placeholder="Internal notes about this request…"
                    rows={3}
                    className="w-full border border-border rounded-lg px-3 py-2 text-[12.5px] bg-background focus:outline-none focus:border-[#006B3C] resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    disabled={approveRequest.isPending}
                    onClick={() => approveRequest.mutate({ id: selected.id, approve: true })}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle size={14} /> Approve
                  </button>
                  <button
                    disabled={approveRequest.isPending}
                    onClick={() => approveRequest.mutate({ id: selected.id, approve: false })}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#FEE8E8] text-[#A32D2D] rounded-lg text-[13px] font-medium hover:bg-red-600 hover:text-white disabled:opacity-50 transition-colors"
                  >
                    <XCircle size={14} /> Reject
                  </button>
                </div>
              </>
            )}

            {/* Provision button, shown after approval or for already-approved requests without an org */}
            {selected?.status === 'provisioned' && !selected?.provisioned_org_id && (
              <button
                onClick={() => {
                  setProvisionRequest(selected)
                  setSelected(null)
                  setProvisionOpen(true)
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#0D2240] text-white rounded-lg text-[13px] font-medium hover:bg-[#0a1a30] transition-colors"
              >
                <Rocket size={14} /> Provision Institution →
              </button>
            )}

            {selected?.status !== 'pending' && selected?.review_notes && (
              <div className="bg-muted/30 rounded-xl p-3 text-[12px] text-muted-foreground">
                <div className="font-medium mb-1">Review notes</div>
                {selected.review_notes}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Provision wizard, pre-filled from the institution request */}
      <CreateOrgWizard
        open={provisionOpen}
        initialName={provisionRequest?.org_name ?? ''}
        onClose={() => { setProvisionOpen(false); setProvisionRequest(null) }}
        onSuccess={async (orgId, orgName) => {
          // Link the request to the new org
          if (provisionRequest) {
            await (supabase.rpc('link_institution_request' as any, {
              p_request_id: provisionRequest.id,
              p_org_id: orgId,
            }) as any)
            qc.invalidateQueries({ queryKey: ['institution-requests'] })
          }
          setProvisionOpen(false)
          setProvisionRequest(null)
          // Immediately open CCO invite sheet
          setAddCcoOrgId(orgId)
          setAddCcoOrgName(orgName)
          setAddCcoOpen(true)
        }}
      />

      <CreateUserSheet
        org_id={addCcoOrgId}
        org_name={addCcoOrgName}
        open={addCcoOpen}
        onClose={() => setAddCcoOpen(false)}
        defaultRole="CCO"
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
          setAddCcoOpen(false)
        }}
      />
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  )
}
